// Libs
import { SWRResponse } from 'swr';
import { AxiosError } from 'axios';
// App
import { ORDER_STATISTICS_URL } from './statistics.constants';
import {
  BestSellerProductSummary,
  LoyalCustomer,
  LoyaltyStatistics,
  OrderStatisticDetails,
  OrderStatisticsDetails,
  PromotionCodeStatistics,
} from './statistics.interfaces';
import { StatisticsPeriod, buildStatisticsPeriodQuery } from '../../../shared';
import { useSWRAxiosGetRequest } from '../../../utils/swr-axios-request.hooks';

import { API_COMMON_BASE_URL } from 'app-shared/lib/api/utils/api.constants';
import { SHOP_ADMINISTRATION_SHOPS_URL } from '../../shop';
import { AxiosHookResponse, useAxiosGetRequest } from '../../../utils/axios-request.hooks';

/**
 * Get order statistics api hook
 */
export const useGetShopOrderStatistics = (shopId: number): AxiosHookResponse<void, OrderStatisticsDetails> => {
  const url = `${API_COMMON_BASE_URL}/${SHOP_ADMINISTRATION_SHOPS_URL}/${shopId}/${ORDER_STATISTICS_URL}`;

  return useAxiosGetRequest(url);
};

/**
 * Get order statistics groupped by month api hook
 */
export const useGetShopOrderStatisticsGroupedByMonth = (
  shopId: number
): AxiosHookResponse<void, OrderStatisticDetails[]> => {
  const url = `${API_COMMON_BASE_URL}/${SHOP_ADMINISTRATION_SHOPS_URL}/${shopId}/${ORDER_STATISTICS_URL}/groups`;

  return useAxiosGetRequest(url);
};

/**
 * Get shop best seller products api hook
 */
export const useGetShopBestSellerProducts = (
  shopId: number
): AxiosHookResponse<void, BestSellerProductSummary[]> => {
  const url = shopId
    ? `${API_COMMON_BASE_URL}/${SHOP_ADMINISTRATION_SHOPS_URL}/${shopId}/${ORDER_STATISTICS_URL}/best-sellers`
    : null;

  return useAxiosGetRequest(url);
};

const buildShopStatisticsUrl = (shopId: number, path: string, period: StatisticsPeriod): string => {
  const url = `${API_COMMON_BASE_URL}/${SHOP_ADMINISTRATION_SHOPS_URL}/${shopId}/${ORDER_STATISTICS_URL}/${path}?${buildStatisticsPeriodQuery(period)}`;

  return url;
};

/**
 * Get the usage of the manual promotion codes of a shop over a period api hook
 */
export const useGetShopPromotionCodeStatistics = (
  shopId: number,
  period: StatisticsPeriod
): SWRResponse<PromotionCodeStatistics, AxiosError> => {
  const urlOptional = (shopId && buildShopStatisticsUrl(shopId, 'promotion-codes', period)) || null;

  return useSWRAxiosGetRequest(urlOptional, { revalidateOnFocus: false });
};

/**
 * Get the loyalty program activity of a shop over a period api hook
 */
export const useGetShopLoyaltyStatistics = (
  shopId: number,
  period: StatisticsPeriod
): SWRResponse<LoyaltyStatistics, AxiosError> => {
  const urlOptional = (shopId && buildShopStatisticsUrl(shopId, 'loyalty', period)) || null;

  return useSWRAxiosGetRequest(urlOptional, { revalidateOnFocus: false });
};

/**
 * Get the customers who earned the most loyalty points of a shop over a period api hook
 */
export const useGetShopMostLoyalCustomers = (
  shopId: number,
  period: StatisticsPeriod
): SWRResponse<LoyalCustomer[], AxiosError> => {
  const urlOptional = (shopId && buildShopStatisticsUrl(shopId, 'loyalty/customers', period)) || null;

  return useSWRAxiosGetRequest(urlOptional, { revalidateOnFocus: false });
};
